import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '@vitesse/base-ui/grid';
import '@vitesse/base-ui/elements/vitesse-search';
import '@vitesse/base-ui/elements/vitesse-dropdown';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-button';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import type { GridColumn } from '@vitesse/base-ui/grid';
import { LocaleController, t } from '@vitesse/base-ui/i18n';
import { pillTabBar, statusBadge } from '@vitesse/base-ui/components/templates';
import { fmtAgo } from '@vitesse/base-ui/format';
import type { FindingFilter, FindingView } from '@vitesse/base-ui/ai';
import type { FindingsManager } from '../../managers/findings-manager';
import { aboutLabel, decisionText, kindLabel, statusLabel, statusTone, type Decision, type FindingNames } from './finding-labels';
import { detailHeight, renderFindingDetail } from './finding-detail';

/*
 * <vitesse-findings-list> — the findings log as a list: three tabs, a search, and one row opened in
 * place. The org's Findings page and a controller's Findings tab are this one element with a
 * different `scope`, so the two never drift into two lists that show the same entry differently.
 *
 * TABS ARE WHAT A PERSON CAME TO DO. Open — a proposal not yet closed, or a follow-up that is due —
 * is the owner's daily view and the page's default; Due is the re-measures that have come round, as
 * the assignment asks them to be seen; All is the history. The numbers are counted by the plane
 * under the same scope and search, so a tab says how much is behind it before it is opened.
 *
 * COMPONENTS NEVER FETCH: every read and decision goes through the FindingsManager, and this
 * re-renders on its `change`. The list is the plane's order — newest first, paged as the reader
 * scrolls — so the columns do not offer a sort that would reorder only the part already loaded.
 */
type Tab = 'open' | 'due' | 'all';

/** A database query per keystroke would put ten on the wire for one word; the reader sees the last. */
const SEARCH_DEBOUNCE_MS = 250;

@customElement('vitesse-findings-list')
export class FindingsList extends LitElement {
  @property({ attribute: false }) manager?: FindingsManager;
  @property({ attribute: false }) names?: FindingNames;
  /** Fixed filters for where the list sits — a controller's tab passes its controller. */
  @property({ attribute: false }) scope: FindingFilter = {};
  @property() defaultTab: Tab = 'open';
  /** The relay and controller filters: on the org's page, not on a controller's tab, where both are fixed. */
  @property({ type: Boolean }) showFilters = false;

  @state() private tab: Tab | null = null;
  @state() private query = '';
  @state() private gatewayId = '';
  @state() private controllerId = '';
  @state() private open: string | null = null;
  @state() private full = new Map<string, FindingView | null>();
  @state() private refs = new Map<string, string>();
  @state() private deciding: { id: string; decision: Decision } | null = null;
  @state() private reason = '';
  @state() private busy = false;
  @state() private decideError = '';

  private searchTimer?: number;
  private readonly onChange = (): void => this.requestUpdate();

  constructor() {
    super();
    new LocaleController(this);
  }

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM: the grid's chrome is global SCSS
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.manager?.addEventListener('change', this.onChange);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.manager?.removeEventListener('change', this.onChange);
    clearTimeout(this.searchTimer);
  }

  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('manager')) {
      (changed.get('manager') as FindingsManager | undefined)?.removeEventListener('change', this.onChange);
      this.manager?.addEventListener('change', this.onChange);
    }
  }

  protected updated(changed: PropertyValues): void {
    // A controller page reuses this element when the reader moves to another controller: a new scope
    // is a new list, never the last one's rows under the new name.
    const scopeMoved = changed.has('scope') && JSON.stringify(changed.get('scope') ?? {}) !== JSON.stringify(this.scope);
    if (changed.has('manager') || scopeMoved) {
      this.open = null;
      void this.load(true);
    }
  }

  private get activeTab(): Tab {
    return this.tab ?? this.defaultTab;
  }

  private base(): FindingFilter {
    return {
      ...this.scope,
      ...(this.query.trim() ? { query: this.query.trim() } : {}),
      ...(this.gatewayId ? { gatewayId: this.gatewayId } : {}),
      ...(this.controllerId ? { controllerId: this.controllerId } : {}),
    };
  }

  /** Read the active tab under the current filters; recount the tabs when the filters changed. */
  private async load(recount: boolean): Promise<void> {
    const m = this.manager;
    if (!m) return;
    const base = this.base();
    if (recount) void m.refreshCounts(base);
    const tab = this.activeTab;
    // The entries read in full go with the list they were read under, as the manager's do: a row
    // still open is read again, so a decision someone else made meanwhile shows on it.
    this.full = new Map();
    this.refs = new Map();
    await m.load({ ...base, ...(tab === 'open' ? { open: true } : tab === 'due' ? { due: true } : {}) });
    if (this.open) await this.readEntry(this.open);
  }

  private pickTab(id: string): void {
    if (id === this.activeTab) return;
    this.tab = id as Tab;
    this.open = null;
    void this.load(false);
  }

  private async openRow(id: string): Promise<void> {
    this.open = this.open === id ? null : id;
    if (this.open) await this.readEntry(id);
  }

  /** An entry in full, and the claims of the entries it cites, read in one call. */
  private async readEntry(id: string): Promise<void> {
    const m = this.manager;
    if (!m) return;
    if (!this.full.has(id)) this.full = new Map(this.full).set(id, await m.read(id));
    const f = this.full.get(id);
    const cited = f ? [...(f.definitionId ? [f.definitionId] : []), ...f.follows].filter((r) => !this.refs.has(r)) : [];
    if (!cited.length) return;
    const read = await m.readMany(cited);
    const refs = new Map(this.refs);
    for (const r of cited) refs.set(r, read.get(r)?.claim ?? '');
    this.refs = refs;
  }

  private async confirmDecision(): Promise<void> {
    const d = this.deciding;
    if (!d || !this.manager || this.busy || !this.reason.trim()) return;
    this.busy = true;
    this.decideError = '';
    const r = await this.manager.decide(d.id, d.decision, this.reason.trim());
    this.busy = false;
    if (!r.ok) {
      this.decideError = r.message;
      return;
    }
    // The decided entry, with its new history, is what the manager now holds in full.
    this.full = new Map(this.full).set(d.id, await this.manager.read(d.id));
    this.deciding = null;
  }

  private columns(): GridColumn<FindingView>[] {
    const names = this.names;
    return [
      { key: 'id', label: t('findings.colId'), width: 64, cell: (f) => html`<b>${f.id}</b>` },
      { key: 'kind', label: t('findings.colKind'), width: 100, cell: (f) => kindLabel(f.kind) },
      { key: 'claim', label: t('findings.colClaim'), cell: (f) => html`<span class="cl-ellipsis" title=${f.claim}>${f.claim}</span>` },
      {
        key: 'status',
        label: t('findings.colStatus'),
        width: 140,
        cell: (f) => (f.status ? statusBadge(statusTone(f.status), statusLabel(f.status)) : nothing),
      },
      { key: 'about', label: t('findings.colAbout'), width: 180, min: 100, cell: (f) => (names ? aboutLabel(f.controllerIds, names) : '') },
      {
        key: 'when',
        label: t('findings.colWhen'),
        width: 100,
        cell: (f) => html`<span title=${new Date(f.createdAtMs).toISOString()}>${fmtAgo(f.createdAtMs)}</span>`,
      },
    ];
  }

  /** The grid's grey bar: the search. */
  private renderToolbar() {
    return html`<vitesse-search
      class="findings-search"
      .placeholder=${t('findings.search')}
      .onQuery=${(q: string) => {
        this.query = q;
        clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(() => void this.load(true), SEARCH_DEBOUNCE_MS);
      }}
    ></vitesse-search>`;
  }

  /**
   * The relay and controller filters, on the tab row rather than in the grid's bar: a menu opened
   * inside the grid is drawn under its rows (vitesse-dialog.ts says why), and out here it is not.
   * With a relay chosen, only its controllers are offered — every relay has a "Core".
   */
  private renderFilters() {
    if (!this.showFilters) return nothing;
    const relays = this.names?.relays ?? [];
    const controllers = (this.names?.controllers ?? []).filter((c) => !this.gatewayId || c.relay === this.gatewayId);
    return html`<div class="findings-filters">
      ${
        relays.length > 1
          ? html`<vitesse-dropdown
              triggerIcon="hard-drive"
              .triggerLabel=${relays.find((r) => r.id === this.gatewayId)?.label ?? t('findings.allRelays')}
              .triggerTitle=${t('findings.relayFilter')}
              .items=${[{ value: '', label: t('findings.allRelays') }, ...relays.map((r) => ({ value: r.id, label: r.label }))]}
              .value=${this.gatewayId}
              @dropdown-select=${(e: CustomEvent<{ value: string }>) => {
                this.gatewayId = e.detail.value;
                // A controller on another relay is not on this one.
                if (this.gatewayId && !this.controllerId.startsWith(`${this.gatewayId}/`)) this.controllerId = '';
                void this.load(true);
              }}
            ></vitesse-dropdown>`
          : nothing
      }
      ${
        controllers.length
          ? html`<vitesse-dropdown
              triggerIcon="cpu"
              .triggerLabel=${controllers.find((c) => c.id === this.controllerId)?.label ?? t('findings.allControllers')}
              .triggerTitle=${t('findings.controllerFilter')}
              .items=${[{ value: '', label: t('findings.allControllers') }, ...controllers.map((c) => ({ value: c.id, label: c.label }))]}
              .value=${this.controllerId}
              @dropdown-select=${(e: CustomEvent<{ value: string }>) => {
                this.controllerId = e.detail.value;
                void this.load(true);
              }}
            ></vitesse-dropdown>`
          : nothing
      }
    </div>`;
  }

  private renderDialog() {
    const d = this.deciding;
    const text = d ? decisionText(d.decision, d.id) : null;
    return html`<vitesse-dialog
      .open=${!!d}
      heading=${text?.heading ?? ''}
      @dialog-close=${() => {
        if (!this.busy) this.deciding = null;
      }}
    >
      <p class="findings-dialog-hint">${text?.hint ?? ''}</p>
      ${this.decideError ? html`<p class="error">${this.decideError}</p>` : nothing}
      <div class="field">
        <label for="finding-reason">${t('findings.reason')}</label>
        <vitesse-text-input
          id="finding-reason"
          multiline
          rows="3"
          .value=${this.reason}
          placeholder=${t('findings.reasonPlaceholder')}
          @input=${(e: Event) => (this.reason = (e.target as HTMLTextAreaElement).value)}
        ></vitesse-text-input>
      </div>
      <vitesse-button slot="footer" color="red" ?disabled=${this.busy} @click=${() => (this.deciding = null)}
        >${t('findings.cancel')}</vitesse-button
      >
      <vitesse-button slot="footer" color="blue" ?disabled=${this.busy || !this.reason.trim()} @click=${() => void this.confirmDecision()}>
        ${this.busy ? t('findings.saving') : (text?.button ?? '')}
      </vitesse-button>
    </vitesse-dialog>`;
  }

  render() {
    const m = this.manager;
    const counts = m?.counts;
    const tab = this.activeTab;
    const filtered = !!(this.query.trim() || this.gatewayId || this.controllerId);
    const today = new Date().toISOString().slice(0, 10);
    return html`${STYLES}
      <div class="entity-tab-header findings-tabs">
        ${pillTabBar({
          tabs: [
            { id: 'open', label: t('findings.tabOpen'), icon: 'bell', count: counts?.open },
            { id: 'due', label: t('findings.tabDue'), icon: 'clock', count: counts?.due },
            { id: 'all', label: t('findings.tabAll'), icon: 'list', count: counts?.all },
          ],
          active: tab,
          onChange: (id) => this.pickTab(id),
        })}
        ${this.renderFilters()}
      </div>
      <vitesse-record-grid
        pageScroll
        .cols=${this.columns()}
        .rows=${m && m.ready ? m.items : null}
        .total=${m?.ready ? m.total : -1}
        .rowLabel=${(f: FindingView) => `${f.id} ${f.claim}`}
        .toolbar=${this.renderToolbar()}
        .emptyLabel=${
          // A log that could not be read is not an empty one: the grid says which, in the one place it
          // says anything about having no rows.
          m?.error
            ? t('findings.loadError', { message: m.error })
            : tab === 'open'
              ? t('findings.emptyOpen')
              : tab === 'due'
                ? t('findings.emptyDue')
                : t('findings.emptyAll')
        }
        .loadingLabel=${t('findings.loading')}
        .noMatchLabel=${t('findings.noMatch')}
        .filtered=${filtered}
        .expanded=${this.open}
        .detailHeight=${(f: FindingView) => detailHeight(this.full.get(f.id) ?? f)}
        .renderDetail=${(f: FindingView) =>
          this.names
            ? renderFindingDetail(f, {
                names: this.names,
                full: this.full.has(f.id) ? this.full.get(f.id) : undefined,
                refs: this.refs,
                today,
                onDecide: (decision) => {
                  this.deciding = { id: f.id, decision };
                  this.reason = '';
                  this.decideError = '';
                },
              })
            : nothing}
        .onPick=${(id: string) => void this.openRow(id)}
        .onReveal=${(i: number) => {
          if (m && m.hasMore && !m.loading && i >= m.items.length - 5) void m.more();
        }}
      ></vitesse-record-grid>
      ${this.renderDialog()}`;
  }
}

/* Light DOM, so a scoped stylesheet is not available: the rules ride with the element, as the tree's
 * do (vitesse-tree.ts), and use only the product's tokens. */
const STYLES = html`<style>
  /* A column with the page's own 1rem rhythm (base-page.scss gives .base-page-content the same gap):
   * the tab row, the grid and the dialog are this element's children, not the page content's, so the
   * page's gap does not reach between them. */
  vitesse-findings-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  vitesse-findings-list .findings-tabs {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-xs);
    flex-wrap: wrap;
  }
  vitesse-findings-list .findings-filters {
    display: flex;
    gap: var(--space-xs);
  }
  vitesse-findings-list .findings-search {
    flex: 1;
    min-width: 0;
  }
  vitesse-findings-list .error {
    color: var(--red);
    font-size: 13px;
    margin: 0 0 var(--space-xs);
  }
  vitesse-findings-list .finding-detail {
    height: 100%;
    overflow: auto;
    padding: var(--space-sm) var(--space-md);
    font-size: 13px;
    line-height: 1.5;
  }
  vitesse-findings-list .finding-detail dl {
    display: grid;
    grid-template-columns: 8rem 1fr;
    gap: var(--space-xs) var(--space-md);
    margin: 0;
  }
  vitesse-findings-list .finding-detail dt,
  vitesse-findings-list .finding-muted {
    color: var(--text-secondary);
  }
  vitesse-findings-list .finding-detail dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  vitesse-findings-list .finding-claim {
    font-weight: 600;
  }
  vitesse-findings-list .finding-due {
    color: var(--red);
    font-weight: 600;
  }
  vitesse-findings-list .finding-ref {
    display: block;
  }
  vitesse-findings-list .finding-run + .finding-run,
  vitesse-findings-list .finding-event + .finding-event {
    margin-top: var(--space-xs);
  }
  vitesse-findings-list .finding-reason {
    font-style: italic;
  }
  vitesse-findings-list .finding-detail details pre {
    max-height: 16rem;
    overflow: auto;
    margin: var(--space-2xs) 0 0;
    padding: var(--space-xs);
    border-radius: var(--radius-md);
    background: var(--surface-raised);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
  }
  vitesse-findings-list .finding-actions {
    display: flex;
    gap: var(--space-xs);
    margin-top: var(--space-sm);
  }
  /* The dialog's body is light DOM too, so its fields are styled here, as the dashboard dialogs
   * style theirs. */
  vitesse-findings-list .findings-dialog-hint {
    margin: 0 0 var(--space-sm);
    color: var(--text-secondary);
  }
  vitesse-findings-list .field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  vitesse-findings-list .field label {
    font-size: 12px;
    font-weight: 600;
    color: var(--text-secondary);
  }
</style>`;

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-findings-list': FindingsList;
  }
}
